import { errorReporting } from "../config";
/**
 * Error-monitoring client: window "error" events, unhandled rejections,
 * (optionally) console.error calls and explicit reports, queued and sent in
 * batches on a timer, or via navigator.sendBeacon when the page is hidden or
 * unloading.
 *
 * Safety guards:
 *  - the reporter never reports errors raised by its own code (re-entrancy flag,
 *    every internal path is wrapped in try/catch);
 *  - a failed batch is retried on the next flush and dropped after
 *    `maxSendAttempts` failures in a row, and the queue is capped (oldest
 *    first), so it cannot grow unbounded.
 */

export type ErrorLevel = "error" | "warning" | "info";

export interface ClientErrorEvent {
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  ts: string;
  level: ErrorLevel;
  meta?: Record<string, unknown>;
}

export interface CaptureOptions {
  stack?: string;
  level?: ErrorLevel;
  meta?: Record<string, unknown>;
}

export interface ErrorReporterOptions {
  /** Full path of the ingestion endpoint. */
  endpoint?: string;
  /** How often the queue is flushed, in ms. Default 10 000. */
  flushIntervalMs?: number;
  /** Max events per request (backend limit). Default 20. */
  maxBatchSize?: number;
  /** Max events held in memory. Default 100. */
  maxQueueSize?: number;
  /** Consecutive send failures before a batch is dropped. Default 3. */
  maxSendAttempts?: number;
  /** Also capture console.error calls (original is always invoked). Default true. */
  captureConsoleError?: boolean;
  /** Release identifier stamped on every event. */
  release?: string;
}

export interface ErrorReporter {
  /** Install global handlers and start the flush timer. Idempotent. */
  init(): void;
  /** Remove handlers, stop timers, clear the queue (used by tests). */
  destroy(): void;
  /** Queue a message-level event. */
  capture(message: string, options?: CaptureOptions): void;
  /** Queue an event built from an Error (or any thrown value). */
  captureException(error: unknown, meta?: Record<string, unknown>): void;
  /** Force a flush. `useBeacon` sends synchronously via sendBeacon. */
  flush(useBeacon?: boolean): void;
  /** Number of events currently queued (used by tests). */
  readonly queueSize: number;
}


export function createErrorReporter(options: ErrorReporterOptions = {}): ErrorReporter {
  const endpoint =
    options.endpoint ?? `${import.meta.env.VITE_API_URL ?? ""}/api/v1/client-errors`;
  const flushIntervalMs = options.flushIntervalMs ?? errorReporting.flushIntervalMs;
  const maxBatchSize = options.maxBatchSize ?? errorReporting.maxBatchSize;
  const maxQueueSize = options.maxQueueSize ?? errorReporting.maxQueueSize;
  const maxSendAttempts = options.maxSendAttempts ?? errorReporting.maxSendAttempts;
  const captureConsole = options.captureConsoleError ?? true;
  const release = options.release ?? import.meta.env.VITE_APP_VERSION ?? "dev";

  let queue: ClientErrorEvent[] = [];
  let initialized = false;
  let destroyed = false;
  let flushTimer: ReturnType<typeof setInterval> | undefined;
  let sendFailures = 0;
  let sendInFlight = false;
  /** Re-entrancy guard: never report errors produced while the reporter runs. */
  let inReporter = false;
  let originalConsoleError: typeof console.error | undefined;

  function buildEvent(message: string, opts: CaptureOptions): ClientErrorEvent {
    const event: ClientErrorEvent = {
      message: message.slice(0, 2000),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ts: new Date().toISOString(),
      level: opts.level ?? "error",
      meta: { release, ...opts.meta },
    };
    if (opts.stack !== undefined) event.stack = opts.stack.slice(0, 8000);
    return event;
  }

  function capture(message: string, opts: CaptureOptions = {}): void {
    if (destroyed || inReporter) return;
    inReporter = true;
    try {
      queue.push(buildEvent(message, opts));
      if (queue.length > maxQueueSize) queue = queue.slice(-maxQueueSize);
    } catch {
      // Never let the reporter throw.
    } finally {
      inReporter = false;
    }
  }

  function captureException(error: unknown, meta?: Record<string, unknown>): void {
    const opts: CaptureOptions = { level: "error", meta: meta ?? {} };
    if (error instanceof Error) {
      if (error.stack !== undefined) opts.stack = error.stack;
      capture(error.message || error.name, opts);
    } else {
      capture(safeString(error), opts);
    }
  }

  function sendWithBeacon(): void {
    if (typeof navigator.sendBeacon !== "function") {
      // Fall back to keepalive fetch; page may be unloading, best-effort only.
      void sendWithFetch();
      return;
    }
    // sendBeacon is cheap and synchronous — drain the whole queue in batches.
    while (queue.length > 0) {
      const batch = queue.splice(0, maxBatchSize);
      const body = new Blob([JSON.stringify({ events: batch })], {
        type: "application/json",
      });
      const accepted = navigator.sendBeacon(endpoint, body);
      if (!accepted) {
        // Browser refused to queue the beacon; the page is likely going away —
        // dropping is the only sensible option.
        queue = [];
        return;
      }
    }
  }

  async function sendWithFetch(): Promise<void> {
    if (sendInFlight || queue.length === 0) return;
    sendInFlight = true;
    const batch = queue.splice(0, maxBatchSize);
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
      if (!res.ok) throw new Error(`client-errors endpoint returned ${res.status}`);
      sendFailures = 0;
    } catch {
      sendFailures += 1;
      if (sendFailures >= maxSendAttempts) {
        // Endpoint unreachable after N tries: drop the batch, start over.
        sendFailures = 0;
      } else {
        queue.unshift(...batch);
      }
    } finally {
      sendInFlight = false;
    }
  }

  function flush(useBeacon = false): void {
    if (destroyed || queue.length === 0) return;
    inReporter = true;
    try {
      if (useBeacon) sendWithBeacon();
      else void sendWithFetch();
    } catch {
      // Never let the reporter throw.
    } finally {
      inReporter = false;
    }
  }

  const onWindowError = (event: ErrorEvent): void => {
    const error: unknown = event.error;
    const opts: CaptureOptions = {
      level: "error",
      meta: {
        source: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    };
    if (error instanceof Error && error.stack !== undefined) opts.stack = error.stack;
    capture(event.message || "Unknown script error", opts);
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason: unknown = event.reason;
    const opts: CaptureOptions = {
      level: "error",
      meta: { source: "unhandledrejection" },
    };
    if (reason instanceof Error) {
      if (reason.stack !== undefined) opts.stack = reason.stack;
      capture(reason.message || reason.name, opts);
    } else {
      capture(safeString(reason), opts);
    }
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") flush(true);
  };

  const onPageHide = (): void => {
    flush(true);
  };

  function init(): void {
    if (initialized || destroyed) return;
    initialized = true;

    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    if (captureConsole) {
      originalConsoleError = console.error;
      console.error = (...args: unknown[]): void => {
        originalConsoleError?.apply(console, args);
        if (inReporter || isEmptyLogRecord(args)) return;
        capture(args.map(safeString).join(" "), {
          level: "error",
          meta: { source: "console.error" },
        });
      };
    }

    flushTimer = setInterval(() => flush(false), flushIntervalMs);
  }

  function destroy(): void {
    if (!initialized) {
      destroyed = true;
      return;
    }
    window.removeEventListener("error", onWindowError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
    if (originalConsoleError) {
      console.error = originalConsoleError;
      originalConsoleError = undefined;
    }
    if (flushTimer !== undefined) clearInterval(flushTimer);
    queue = [];
    initialized = false;
    destroyed = true;
  }

  return {
    init,
    destroy,
    capture,
    captureException,
    flush,
    get queueSize(): number {
      return queue.length;
    },
  };
}

/**
 * A pino log record that says nothing: `{"time":…,"level":50}` and no other
 * key. The WalletConnect packages log through @walletconnect/logger, whose
 * browser pino (`asObject`) builds each record by `Object.assign`-ing the
 * logged object into `{ time, level }`; an `Error` has no enumerable own
 * properties, so `logger.error(err)` loses the message and the stack and
 * forwards exactly this to `console.error` — on every closed QR sheet, among
 * others (`EthereumProvider.connect()`'s catch logs the "Connection request
 * reset" it then rethrows). The error it stood for was already lost before it
 * got here, so dropping the record hides nothing; the failures that matter
 * reach the app as rejected promises, and are reported where they are caught.
 * A record with ANY other key (`msg`, a context, an error serialized with its
 * fields) is still reported.
 */
function isEmptyLogRecord(args: readonly unknown[]): boolean {
  if (args.length !== 1) return false;
  let record: unknown = args[0];
  if (typeof record === "string") {
    if (!record.startsWith("{")) return false;
    try {
      record = JSON.parse(record) as unknown;
    } catch {
      return false;
    }
  }
  if (record === null || typeof record !== "object" || Array.isArray(record)) return false;
  const keys = Object.keys(record);
  return (
    keys.includes("level") &&
    typeof (record as { level?: unknown }).level === "number" &&
    keys.every((key) => key === "time" || key === "level")
  );
}

function safeString(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/** App-wide singleton. Initialized first thing in main.tsx. */
export const errorReporter: ErrorReporter = createErrorReporter();
